/**
 * The editor shows spaces as typed (white-space: pre-wrap), but that CSS doesn't travel with
 * copied HTML, so other editors collapse leading spaces and runs of spaces. These rewrite the
 * text in clipboard HTML so the spaces survive, and undo that when it's pasted back in.
 */

/**
 * Set on the first element of HTML that preserveSpaces rewrote. Its value lists which
 * non-breaking spaces (counted in text order) were in the text already, so a paste back
 * into Quill keeps those and turns the rest back into spaces.
 */
export const SPACES_ATTR = "data-quill-spaces";

// A comment, a tag (attribute values may hold ">"), a run of text, or a stray "<".
const TOKEN = /<!--[\s\S]*?-->|<(?:[^>"']|"[^"]*"|'[^']*')*>|[^<]+|</g;
// Tags that start a new line: blocks, and <br> within one.
const LINE_BREAK = /^<\/?(p|h[1-6]|li|ul|ol|blockquote|div|pre|hr|br|table|tr|td|th)\b/i;
const PRE_OPEN = /^<pre\b/i;
const PRE_CLOSE = /^<\/pre\b/i;
const OPEN_TAG = /^<[a-z][^\s/>]*/i;
const MARKED = new RegExp(`<[^>]*\\s${SPACES_ATTR}="([\\d,]*)"`, "i");
// One character of text, an entity counting as one.
const CHAR = /&(?:#\d+|#x[\da-f]+|[a-z]+\d*);|[\s\S]/gi;
const NBSP = /^(?:\u00a0|&nbsp;|&#160;|&#x0*a0;)$/i;

/**
 * Rewrites a selection's HTML so its spaces survive HTML parsing: spaces at the start of a
 * line become &nbsp;, and in a run of spaces the first stays a space (so lines still wrap)
 * and the rest become &nbsp;. Single spaces between words, and <pre>, are left alone.
 */
export function preserveSpaces(html: string): string {
  const own: number[] = [];
  let nbsps = 0;
  const out = rewriteText(html, (char, line) => {
    if (char === " ") {
      const space = line.start || line.inRun ? "&nbsp;" : " ";
      if (space !== " ") nbsps++;
      line.inRun = true;
      return space;
    }
    line.start = line.inRun = false;
    if (NBSP.test(char)) own.push(nbsps++);
    return char;
  });
  const mark = ` ${SPACES_ATTR}="${own.join(",")}"`;
  const first = out.match(TOKEN)?.find((t) => OPEN_TAG.test(t));
  if (!first) return `<span${mark}>${out}</span>`;
  const at = out.indexOf(first) + first.match(OPEN_TAG)![0].length;
  return out.slice(0, at) + mark + out.slice(at);
}

/**
 * Undoes preserveSpaces, for HTML copied out of Quill and pasted back in, so the document
 * gets plain spaces again. HTML that preserveSpaces didn't mark is returned unchanged.
 */
export function restoreSpaces(html: string): string {
  const marked = html.match(MARKED);
  if (!marked) return html;
  const own = new Set(marked[1]!.split(",").filter(Boolean).map(Number));
  let nbsps = 0;
  return rewriteText(html, (char) => (NBSP.test(char) && !own.has(nbsps++) ? " " : char));
}

interface Line {
  /** Nothing but spaces since the line began. */
  start: boolean;
  /** The last character was a space. */
  inRun: boolean;
}

function rewriteText(html: string, rewrite: (char: string, line: Line) => string): string {
  const line: Line = { start: true, inRun: false };
  let preDepth = 0;
  let out = "";
  for (const [token] of html.matchAll(TOKEN)) {
    if (token.startsWith("<") && token.length > 1) {
      if (PRE_OPEN.test(token)) preDepth++;
      else if (PRE_CLOSE.test(token)) preDepth = Math.max(0, preDepth - 1);
      if (LINE_BREAK.test(token)) {
        line.start = true;
        line.inRun = false;
      }
      out += token;
    } else if (preDepth > 0) {
      out += token;
    } else {
      // Line endings only appear between tags (the clipboard's own wrapping): pass them by.
      for (const [char] of token.matchAll(CHAR)) out += char === "\n" || char === "\r" ? char : rewrite(char, line);
    }
  }
  return out;
}
